import * as Sentry from '@sentry/react';
import { format } from 'date-fns';

interface MergedValues {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

type MergedValuesArg = MergedValues | null | undefined;

// taken from: https://stackoverflow.com/questions/51603250/typescript-3-parameter-list-intersection-type/51604379#51604379
type TupleTypes<T> = { [P in keyof T]: T[P] } extends { [key: number]: infer V }
  ? NullToObject<V>
  : never;
type NullToObject<T> = T extends null | undefined ? {} : T;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type UnionToIntersection<U> = (U extends any ? (k: U) => void : never) extends (
  k: infer I,
) => void
  ? I
  : never;

/**
 * @link https://github.com/adobe/react-spectrum/blob/main/packages/%40react-aria/utils/src/mergeProps.ts
 *
 *
 * Merges multiple values together. the last prop object overrides all previous ones.
 * @param args - Multiple sets of props to merge together.
 */
export function mergeValues<T extends MergedValuesArg[]>(
  ...args: T
): UnionToIntersection<TupleTypes<T>> {
  // Start with a base clone of the first argument. This is a lot faster than starting
  // with an empty object and adding properties as we go.
  // eslint-disable-next-line prefer-const
  let result: MergedValues = { ...args[0] };
  for (let i = 1; i < args.length; i++) {
    // eslint-disable-next-line prefer-const
    let props = args[i];
    // eslint-disable-next-line prefer-const
    for (let key in props) {
      const a = result[key];
      const b = props[key];
      result[key] = b || a;
    }
  }

  return result as UnionToIntersection<TupleTypes<T>>;
}

export const formattedSubString = (str: string, length = 12) => {
  if (str.length <= length - 1) return str;
  return str.substring(0, length - 1) + '...';
};

export const toKebabCase = (str: string): string => {
  return str.toLowerCase().replaceAll(/\s+/g, '-');
};

const defaultDateToken = 'MMM dd, yyyy';

/**
 * For displaying read-only timestamps (e.g. `created_at`, `updated_at`) use
 * `<FormattedDateTime>` from `~/features/timezone/FormattedDateTime` instead —
 * it respects the user's preferred timezone from TimezoneContext.
 *
 * Use this function only for non-display purposes (e.g. API payload
 * formatting with an explicit formatToken like `'yyyy-MM-dd'`).
 */
export const formatDate = (date: Date, formatToken?: string) => {
  return format(date, formatToken ?? defaultDateToken);
};

const defaultTimeToken = 'hh:mm aa';
export const formatTime = (date: Date, formatToken?: string) => {
  return format(date, formatToken ?? defaultTimeToken);
};

export const formatDateTime = (date: Date, formatToken?: string) => {
  return format(date, formatToken ?? `${defaultDateToken} ${defaultTimeToken}`);
};

export const toPayloadTimeFormat = (
  date: Date,
  options?: { includeSeconds: boolean },
) => {
  return formatTime(date, options?.includeSeconds ? 'HH:mm:ss' : 'HH:mm');
};

export const isoTimezoneRangeForLocalDate = (localDate: Date) => {
  const startOfDayLocal = new Date(localDate);
  startOfDayLocal.setHours(0, 0, 0, 0);
  const endOfDayLocal = new Date(localDate);
  endOfDayLocal.setHours(23, 59, 59, 999);

  const startOfDayISO = startOfDayLocal.toISOString();
  const endOfDayISO = endOfDayLocal.toISOString();

  return { startOfDayISO, endOfDayISO };
};

export const toPayloadDateFormat = (date: Date) => {
  return formatDate(date, 'yyyy-MM-dd');
};

export const stripUndefined = (obj: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
}): { [key: string]: any } => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const result: { [key: string]: any } = {};
  for (const key in obj) {
    if (obj[key] !== undefined) {
      result[key] = obj[key];
    }
  }
  return result;
};

export const stripNull = (obj: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
}): { [key: string]: any } => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const result: { [key: string]: any } = {};
  for (const key in obj) {
    if (obj[key] !== null) {
      result[key] = obj[key];
    }
  }
  return result;
};

export function logAuthAttempt({
  username = 'Unknown User',
  authType = 'LOGIN',
  success = true,
  errorMessage,
}: {
  username?: string;
  authType?: string;
  success?: boolean;
  errorMessage?: string;
}) {
  Sentry.captureMessage(
    `${authType} attempt for user '${username}' - ${
      success ? 'SUCCESS' : 'FAILURE'
    }`,
    {
      level: success ? 'info' : 'warning',
      tags: {
        auth_type: authType,
        auth_status: success ? 'success' : 'failure',
        event_type: 'auth_attempt',
      },
      extra: {
        username,
        timestamp: new Date().toISOString(),
        userAgent:
          typeof navigator === 'undefined' ? 'unknown' : navigator.userAgent,
        errorMessage,
      },
    },
  );
}

/**
 * Strips HTML tags from a string and returns plain text while preserving line breaks
 * @param html - The HTML string to strip tags from
 * @returns Plain text without HTML tags but with preserved line breaks
 */
export const stripHtmlTags = (html: string): string => {
  if (!html) return '';

  // This function should never be used in SSR environments
  if (typeof document === 'undefined') {
    throw new TypeError(
      'stripHtmlTags should not be used in SSR environments. Use a server-safe alternative for HTML stripping.',
    );
  }

  // First, convert common HTML line break tags to newline characters
  const processedHtml = html
    .replaceAll(/<br\s*\/?>/gi, '\n') // Convert <br> and <br/> to newlines
    .replaceAll(/<\/p>/gi, '\n') // Convert paragraph endings to newlines
    .replaceAll(/<\/div>/gi, '\n') // Convert div endings to newlines
    .replaceAll(/<\/li>/gi, '\n') // Convert list item endings to newlines
    .replaceAll(/<\/h[1-6]>/gi, '\n'); // Convert heading endings to newlines

  // Create a temporary DOM element to parse HTML
  const temp = document.createElement('div');
  temp.innerHTML = processedHtml;
  // Get the text content and clean up multiple consecutive newlines
  const textContent = temp.textContent || temp.innerText || '';
  // Clean up multiple consecutive newlines and trim
  return textContent
    .replaceAll(/\n\s*\n\s*\n/g, '\n\n') // Replace 3+ newlines with 2
    .trim();
};
